import { useState, useEffect } from 'react';
import axios from 'axios';

interface EdgeData {
  scoredTrades: number;
  totalClosed: number;
  pendingResolution: number;
  winRate: number | null;
  breakEvenWinRate: number | null;
  avgEdgePerShare: number | null;
  last20: { n: number; winRate: number | null; avgEdge: number | null };
  brier: { n: number; model: number; market: number } | null;
  calibration: { range: string; n: number; predicted: number; actual: number }[];
  closeReasons: Record<string, { n: number; pnl: number }>;
}

// Minimum resolved sample before the verdict is worth taking seriously
const MIN_SAMPLE = 30;

function verdict(d: EdgeData): { label: string; cls: string; detail: string } {
  if (d.scoredTrades === 0) {
    return { label: 'NO DATA YET', cls: 'edge-neutral', detail: 'No trades have been scored against a final market resolution yet.' };
  }
  const small = d.scoredTrades < MIN_SAMPLE;
  const hasEdge = (d.avgEdgePerShare ?? 0) > 0;
  const beatsMarket = d.brier ? d.brier.model < d.brier.market : false;
  if (hasEdge && beatsMarket) {
    return {
      label: small ? 'EARLY POSITIVE — SAMPLE TOO SMALL' : 'EDGE DETECTED',
      cls: small ? 'edge-warn' : 'edge-good',
      detail: small
        ? `Positive edge so far, but ${d.scoredTrades} resolved trades is too few to trust (need ${MIN_SAMPLE}+).`
        : `The AI is both beating its entry prices and out-predicting the market over ${d.scoredTrades} resolved trades.`,
    };
  }
  if (hasEdge) {
    return {
      label: small ? 'INCONCLUSIVE — SAMPLE TOO SMALL' : 'WEAK EDGE',
      cls: 'edge-warn',
      detail: 'Win rate beats break-even, but the model is not out-predicting market prices (Brier). Could be luck.',
    };
  }
  return {
    label: small ? 'EARLY NEGATIVE — SAMPLE TOO SMALL' : 'NO EDGE',
    cls: small ? 'edge-warn' : 'edge-bad',
    detail: `The AI is winning less often than its entry prices require to break even (${d.scoredTrades} resolved trades).`,
  };
}

export default function EdgeReport() {
  const [data, setData] = useState<EdgeData | null>(null);

  useEffect(() => {
    const load = () => axios.get('/api/edge').then(r => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;
  const v = verdict(data);
  const pct = (x: number | null | undefined) => x == null ? '—' : `${(x * 100).toFixed(1)}%`;
  const cents = (x: number | null | undefined) => x == null ? '—' : `${x >= 0 ? '+' : ''}${(x * 100).toFixed(1)}¢`;

  return (
    <div className="panel edge-panel">
      <div className="panel-header">
        🔬 Edge Report
        <span className={`edge-verdict ${v.cls}`}>{v.label}</span>
        <span className="panel-count">{data.scoredTrades} scored</span>
      </div>
      <div className="edge-detail">{v.detail}</div>

      <div className="edge-stats-row">
        <div className="edge-stat">
          <div className="edge-stat-label">Win rate</div>
          <div className="edge-stat-value">{pct(data.winRate)}</div>
          <div className="edge-stat-sub">need {pct(data.breakEvenWinRate)} to break even</div>
        </div>
        <div className="edge-stat">
          <div className="edge-stat-label">Avg edge / share</div>
          <div className={`edge-stat-value ${(data.avgEdgePerShare ?? 0) >= 0 ? 'positive' : 'negative'}`}>
            {cents(data.avgEdgePerShare)}
          </div>
          <div className="edge-stat-sub">resolution value − entry price</div>
        </div>
        <div className="edge-stat">
          <div className="edge-stat-label">Last {data.last20.n}</div>
          <div className={`edge-stat-value ${(data.last20.avgEdge ?? 0) >= 0 ? 'positive' : 'negative'}`}>
            {pct(data.last20.winRate)}
          </div>
          <div className="edge-stat-sub">edge {cents(data.last20.avgEdge)}</div>
        </div>
        <div className="edge-stat">
          <div className="edge-stat-label">Brier (lower = better)</div>
          <div className="edge-stat-value">
            {data.brier ? data.brier.model.toFixed(3) : '—'}
            {data.brier && (
              <span className={data.brier.model < data.brier.market ? 'positive' : 'negative'}>
                {' '}{data.brier.model < data.brier.market ? 'beats' : 'loses to'} market ({data.brier.market.toFixed(3)})
              </span>
            )}
          </div>
          <div className="edge-stat-sub">prediction accuracy vs the price itself</div>
        </div>
        <div className="edge-stat">
          <div className="edge-stat-label">Awaiting resolution</div>
          <div className="edge-stat-value">{data.pendingResolution}</div>
          <div className="edge-stat-sub">of {data.totalClosed} closed trades</div>
        </div>
      </div>

      {data.calibration.length > 0 && (
        <div className="edge-calibration">
          <div className="edge-section-title">Calibration — when the AI says X%, how often is it right?</div>
          <table className="edge-table">
            <thead>
              <tr><th>Predicted</th><th>Bets</th><th>Avg predicted</th><th>Actual win rate</th></tr>
            </thead>
            <tbody>
              {data.calibration.map(c => (
                <tr key={c.range}>
                  <td>{c.range}</td>
                  <td>{c.n}</td>
                  <td>{c.predicted.toFixed(0)}%</td>
                  <td className={c.actual >= c.predicted - 5 ? 'positive' : 'negative'}>{c.actual.toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {Object.keys(data.closeReasons).length > 0 && (
        <div className="edge-calibration">
          <div className="edge-section-title">P&L by exit type</div>
          <table className="edge-table">
            <thead>
              <tr><th>Exit</th><th>Trades</th><th>Total P&L</th></tr>
            </thead>
            <tbody>
              {Object.entries(data.closeReasons).sort((a, b) => b[1].n - a[1].n).map(([k, r]) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td>{r.n}</td>
                  <td className={r.pnl >= 0 ? 'positive' : 'negative'}>{r.pnl >= 0 ? '+' : ''}${r.pnl.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
